/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useMemo, useRef } from 'react';

import { Dashboard } from '@uppy/react';
import type { DashboardProps as UppyDashboardProps } from '@uppy/react/src/Dashboard';

import { LoadingItem, typography2 } from '@coursera/cds-core';

import type {
  PropsToUppyAssetUploader,
  RenderProps,
  UppyEventHandlerProps,
} from 'bundles/asset-admin/components/UppyAssetUploader';
import UppyAssetUploader from 'bundles/asset-admin/components/UppyAssetUploader';
import { DEFAULT_PLUGIN_SOURCES } from 'bundles/asset-admin/constants/AssetUpload';
import type { PluginSourcesType } from 'bundles/asset-admin/constants/AssetUpload';
import { useDisableFocusLock } from 'bundles/asset-admin/context/DisableFocusLockContext';
import type { Locale, TMeta, UppyRef } from 'bundles/asset-admin/types/uppy';
import AssetAdminTracking from 'bundles/asset-admin/utils/AssetAdminTracking';
import { mapAssetTypeToMimes } from 'bundles/asset-admin/utils/AssetUploaderUtils';
import { useCreateGoogleDriveMutationObserver } from 'bundles/asset-admin/utils/useCreateGoogleDriveMutationObserver';
import type { Assembly, TransloaditFileMap } from 'bundles/authoring/common/types/Upload';

import _t from 'i18n!nls/asset-admin';

// eslint-disable-next-line no-restricted-syntax, import/extensions
import 'css!@uppy/core/dist/style.min.css';
// eslint-disable-next-line no-restricted-syntax, import/extensions
import 'css!@uppy/dashboard/dist/style.min.css';
// eslint-disable-next-line no-restricted-syntax, import/extensions
import 'css!@uppy/url/dist/style.min.css';

// TODO: When updating the uppy version, we will need to test that these styles are applied as expected.
const styles = {
  hideExtraFeatures: css`
    .uppy-DashboardContent-bar button:not(.uppy-c-btn-primary),
    .uppy-StatusBar button:not(.uppy-c-btn-primary) {
      display: none;
    }
  `,

  dashboardUploader: css`
    .uppy-size--md .uppy-Dashboard-AddFiles-list {
      max-width: 90%;
    }

    .uppy-Dashboard {
      font-family: inherit;
      display: flex;
      justify-content: center;
      align-items: center;

      .uppy-Dashboard-innerWrap > .uppy-Dashboard-AddFiles {
        border: none;
      }

      .uppy-Dashboard-close {
        display: none;
      }

      .uppy-Dashboard-inner {
        background: var(--cds-color-neutral-background-primary);
        border: 1px var(--cds-color-interactive-primary) dashed;
        border-radius: 0;

        .uppy-c-btn {
          border-radius: 0;
        }

        .uppy-c-btn-primary {
          background-color: var(--cds-color-interactive-primary);

          &:hover {
            background-color: var(--cds-color-interactive-primary-hover);
          }
        }
      }

      .uppy-Dashboard-dropFilesTitle,
      .uppy-Dashboard-AddFiles-title {
        font-size: 24px;
        line-height: 28px;
        color: var(--cds-color-neutral-primary);
        max-width: 420px;
        margin-bottom: var(--cds-spacing-400);
      }

      .uppy-DashboardTab {
        margin: 0 8px;

        .uppy-DashboardTab-btn {
          width: 92px;
        }

        svg {
          width: 48px;
          height: 48px;
        }

        .uppy-DashboardTab-name {
          color: var(--cds-color-neutral-primary);
          font-size: 14px;
          line-height: 24px;
        }
      }

      .uppy-Url {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .uppy-Url-input {
          width: 90%;
          max-width: 650px;
          margin-bottom: var(--cds-spacing-200);
        }
      }
    }
  `,
  hideDoneState: css`
    .uppy-StatusBar-actions .uppy-StatusBar-actionBtn--done,
    .uppy-DashboardContent-title,
    .uppy-StatusBar-statusPrimary svg {
      display: none;
    }
  `,
  loadingOverlay: css`
    position: relative;
    top: -35px;

    /*  needs to be above uppy overlay */
    z-index: 1001;
    left: 12px;
    max-width: 50%;

    h3 {
      ${typography2.bodyPrimary}
    }
  `,
};

/*
 * Wrapper component around the Uppy Dashboard (https://uppy.io/docs/dashboard/) file uploader
 * It declares event handlers to both prepare files for uploading after they have been selected
 * and to save assets after their transloadit (https://transloadit.com/docs/) assemblies have completed.
 *
 * We override the proptypes here https://github.com/transloadit/uppy/blob/main/packages/%40uppy/react/src/propTypes.js#L2 which is referencing the child dependency of @uppy/react (because of the version resolution we enforce) rather than the sibling @uppy/core we instantiate from (so the instanceOf check fails).
 */

export type Props = Omit<PropsToUppyAssetUploader, 'uppyProps'> & {
  autoProceed?: boolean;
  // null = do not use plugin sources
  pluginSources?: Array<PluginSourcesType> | null;
  hideUploadButton?: boolean;
  hideExtraFeatures?: boolean;
  onDashboardMount?: () => void;
  onDashboardUnmount?: () => void;
} & Partial<UppyEventHandlerProps> &
  Partial<Pick<UppyDashboardProps<TMeta, {}>, 'uppy' | 'height' | 'width'>>;

export class DashboardUploader extends React.Component<Props> {
  static defaultProps = {
    onUploadStart: (files: TransloaditFileMap) => files,
    onUploadComplete: (assembly: Assembly) => assembly,
    onUploadError: (assembly: Assembly) => assembly,
    onAssetCreationComplete: (assetIds: string[]) => assetIds,
    onAssetCreationError: (error: Error) => error,
  };

  componentDidMount() {
    const { uppyRef, onDashboardMount } = this.props;

    if (uppyRef) {
      uppyRef.current = this.uppyRef.current;
    }
    onDashboardMount?.();
  }

  componentWillUnmount(): void {
    const { onDashboardUnmount } = this.props;

    onDashboardUnmount?.();
  }

  handleUploadError = (assembly: Assembly, error: Error | undefined | null): void => {
    const { onUploadError, assetType } = this.props;

    if (onUploadError) {
      onUploadError(assembly, error);
    }

    AssetAdminTracking.track('upload', 'failed', { assetType });
  };

  handleAssetCreationComplete = (assetIds: Array<string>, assembly: Assembly): void | Promise<void> => {
    const { onAssetCreationComplete } = this.props;

    if (onAssetCreationComplete) {
      return onAssetCreationComplete(assetIds, assembly);
    }

    return undefined;
  };

  handleOnFileUploadComplete: UppyEventHandlerProps['onFileUploadComplete'] = (assembly) => {
    const { onFileUploadComplete } = this.props;

    onFileUploadComplete?.(assembly);
  };

  getLocale = (): Locale => {
    const { locale, allowedFileTypes, assetType, showPostAssemblyLoader } = this.props;
    const fileTypes = (allowedFileTypes || (assetType && mapAssetTypeToMimes(assetType)) || []).join(', ');
    const acceptedFileTypes =
      fileTypes.length > 0 ? _t('The following file types are accepted:  #{fileTypes}', { fileTypes }) : '';

    return {
      strings: {
        youCanOnlyUploadFileTypes: _t('Unsupported file type. #{acceptedFileTypes}', {
          acceptedFileTypes,
        }),
        ...(showPostAssemblyLoader
          ? {
              complete: '',
              done: '',
            }
          : {}),

        ...locale?.strings,
      },
    };
  };

  uppyRef: UppyRef = { current: undefined };

  render() {
    const {
      courseId,
      assetContext,
      templateId,
      uploadToAssetService,
      uploadPaused,
      allowMultiple,
      allowedFileTypes,
      assetType,
      autoProceed,
      height,
      width,
      onFileAdded,
      onFilesReadyForUpload,
      onUploadStart,
      onUploadProgress,
      onUploadSuccess,
      onAssetCreationError,
      onUploadsComplete,
      pluginSources,
      assetCreationUrl,
      additionalAssetTags,
      showPostAssemblyLoader,
      hideUploadButton,
      hideExtraFeatures = false,
      transloaditPluginFields,
    } = this.props;

    return (
      <div
        className="rc-DashboardUploader"
        css={[
          styles.dashboardUploader,
          hideExtraFeatures && styles.hideExtraFeatures,
          showPostAssemblyLoader && styles.hideDoneState,
        ]}
      >
        <UppyAssetUploader
          courseId={courseId}
          assetContext={assetContext}
          assetCreationUrl={assetCreationUrl}
          additionalAssetTags={additionalAssetTags}
          uploadToAssetService={uploadToAssetService}
          uploadPaused={uploadPaused}
          templateId={templateId}
          uppyProps={{ autoProceed }}
          allowMultiple={allowMultiple}
          allowedFileTypes={allowedFileTypes}
          assetType={assetType}
          onFileAdded={onFileAdded}
          onFilesReadyForUpload={onFilesReadyForUpload}
          onUploadStart={onUploadStart}
          onUploadProgress={onUploadProgress}
          onUploadError={this.handleUploadError}
          onUploadSuccess={onUploadSuccess}
          onAssetCreationError={onAssetCreationError}
          onAssetCreationComplete={this.handleAssetCreationComplete}
          onFileUploadComplete={this.handleOnFileUploadComplete}
          onUploadsComplete={onUploadsComplete}
          uppyRef={this.uppyRef}
          showPostAssemblyLoader={showPostAssemblyLoader}
          transloaditPluginFields={transloaditPluginFields}
          locale={this.getLocale()}
          pluginSources={pluginSources}
          render={({ uppy }: RenderProps) => (
            <div className="uppy-wrapper">
              <Dashboard
                uppy={uppy}
                height={height ?? 400}
                width={width ?? 786}
                plugins={pluginSources === null ? [] : pluginSources}
                showLinkToFileUploadResult={!hideExtraFeatures}
                showProgressDetails={!hideExtraFeatures}
                hideRetryButton={hideExtraFeatures}
                hidePauseResumeButton={hideExtraFeatures}
                hideCancelButton={hideExtraFeatures}
                {...(hideUploadButton ? { hideUploadButton } : {})}
                proudlyDisplayPoweredByUppy={false}
              />
              {showPostAssemblyLoader && (
                <div css={styles.loadingOverlay}>
                  <LoadingItem label={_t('processing...')} />
                </div>
              )}
            </div>
          )}
        />
      </div>
    );
  }
}

/**
 * @deprecated
 * Use the version in cds/DashboardUploader instead to get cds overrides.
 */
const DashboardUploaderWithFocusOverride = ({
  onDashboardMount: onDashboardMountFromProps,
  onDashboardUnmount: onDashboardUnmountFromProps,
  pluginSources = DEFAULT_PLUGIN_SOURCES,
  ...props
}: Props) => {
  const context = useDisableFocusLock();
  const { setDisableFocusLock } = context || {};
  const googleDriveEnabled = useMemo(() => pluginSources?.find((source) => source === 'GoogleDrive'), [pluginSources]);

  const onGooglePickerSelected = useCallback<() => void>(() => {
    setDisableFocusLock?.(true);
  }, [setDisableFocusLock]);

  const createGoogleDriveMutationObserver = useCreateGoogleDriveMutationObserver(onGooglePickerSelected);

  const observationRef = useRef<MutationObserver | null>(null);

  const onDashboardMount = useCallback<() => void>(() => {
    if (googleDriveEnabled) {
      // debugger;
      observationRef.current = createGoogleDriveMutationObserver();
    }
    onDashboardMountFromProps?.();
  }, [createGoogleDriveMutationObserver, googleDriveEnabled, onDashboardMountFromProps]);

  const onDashboardUnmount = useCallback<() => void>(() => {
    if (googleDriveEnabled) {
      observationRef.current?.disconnect();
      setDisableFocusLock?.(false);
    }
    onDashboardUnmountFromProps?.();
  }, [googleDriveEnabled, onDashboardUnmountFromProps, setDisableFocusLock]);

  return (
    <DashboardUploader
      {...props}
      onDashboardMount={onDashboardMount}
      onDashboardUnmount={onDashboardUnmount}
      pluginSources={pluginSources}
    />
  );
};

export default DashboardUploaderWithFocusOverride;
